import { Frustum, Sphere, Vector3 } from 'three';

import { isDefined } from '../../../../utils/variables';
import { SkyviewOrthographicCamera } from '../../../cameras/skyview-orthographic-camera';
import { SkyviewCamera } from '../../../cameras/types';
import { TileNode } from './types';

export const extractTileNodeBoundingSphere = (
  tileNode: TileNode,
  offset: Vector3 = new Vector3(),
) => {
  const { sphere: tileSphere, box: tileBox } = tileNode.boundingVolume;

  if (tileSphere && tileSphere.length >= 4) {
    return new Sphere(
      new Vector3(tileSphere[0] + offset.x, tileSphere[1] + offset.y, tileSphere[2] + offset.z),
      tileSphere[3],
    );
  }

  if (tileBox && tileBox.length >= 12) {
    const cx = tileBox[0] + offset.x;
    const cy = tileBox[1] + offset.y;
    const cz = tileBox[2] + offset.z;
    // Half-axes vectors summed to get the corner offset, radius is its magnitude
    const rx = tileBox[3] + tileBox[6] + tileBox[9];
    const ry = tileBox[4] + tileBox[7] + tileBox[10];
    const rz = tileBox[5] + tileBox[8] + tileBox[11];
    const radius = Math.sqrt(rx * rx + ry * ry + rz * rz);
    return new Sphere(new Vector3(cx, cy, cz), radius);
  }

  throw new Error(`TileNode does not have a valid bounding sphere or box.`);
};

export function recalculateCameraHeight(
  camera: SkyviewCamera,
  tileNode: TileNode,
  offset?: Vector3,
) {
  if (camera instanceof SkyviewOrthographicCamera) {
    const tileOffset = tileNode.boundingVolume?.sphere?.[2] ?? 0;
    return camera.calculatedDistance + tileOffset + (offset?.z ?? 0);
  }

  return camera.getTHREECamera().getWorldPosition(new Vector3()).z;
}

export function isTileSetNode(node: TileNode) {
  return node.content?.uri?.includes('tileset') ?? false;
}

export function createTileNodeDataUri(tileNode: TileNode, uri: string) {
  return `${tileNode.parentTileSet.relativePath}${uri}`;
}

export function getNodeWithContent(node: TileNode): TileNode | undefined {
  if (isDefined(node.content?.uri)) {
    return node;
  }

  for (const child of node.children) {
    const nodeWithContent = getNodeWithContent(child);
    if (isDefined(nodeWithContent)) {
      return nodeWithContent;
    }
  }

  return undefined;
}

/**
 * Evaluates if a node is within the frustum and also wether it is within range of the camera.
 */
export function evaluateTileNodeForCulling(
  worldPosition: Vector3,
  frustum: Frustum,
  tile: TileNode,
  offset: Vector3 = new Vector3(),
) {
  const sphere = extractTileNodeBoundingSphere(tile, offset);
  const withinRange = worldPosition.distanceTo(sphere.center) <= sphere.radius * 3;
  const withinFrustum = frustum.intersectsSphere(sphere);
  return { withinRange, withinFrustum };
}
